import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Shield, Settings, LogOut, ToggleLeft, ToggleRight, Search, ChevronUp, ChevronDown, Eye, Trash2, UserMinus, UserCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Utility function to check if JWT token is expired
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Error parsing token:', error);
    return true;
  }
};

const AdminPanel = ({ adminToken, onLogout }) => {
  const [featureFlags, setFeatureFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState('');
  const [currentTab, setCurrentTab] = useState('feature-flags');
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedEventType, setSelectedEventType] = useState('all');

  // ✅ Phase 2: Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // Separate pagination for logs and users
  const [userPage, setUserPage] = useState(1);
  const [logPage, setLogPage] = useState(1);
  const [userTotalPages, setUserTotalPages] = useState(1);
  const [logTotalPages, setLogTotalPages] = useState(1);
  
  // ✅ Phase 2: Search and filter states  
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: '', direction: 'asc' });
  
  // ✅ Phase 2: Modal states
  const [showLogDetailModal, setShowLogDetailModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteMode, setDeleteMode] = useState('disable'); // 'disable', 'soft', or 'hard'

  // Subscription plans for feature flags
  const subscriptionPlans = [
    { value: 'free', label: 'Free Plan' },
    { value: 'basic', label: 'Basic Plan' },
    { value: 'premium', label: 'Premium Plan' },
    { value: 'professional', label: 'Professional Plan' },
    { value: 'enterprise', label: 'Enterprise Plan' }
  ];

  // Feature groups as defined in requirements
  const featureGroups = [
    {
      id: 'master_flag',
      name: 'Master Flag',
      description: 'Shows all buttons/tabs when enabled for current session',
      flags: ['master_flag'],
      isMaster: true,
      subscriptionPlan: 'free'
    },
    {
      id: 'college_planning',
      name: 'College Planning',
      description: 'College planning features and tools',
      flags: ['college_planning'],
      subscriptionPlan: 'basic'
    },
    {
      id: 'subscription_plans',
      name: 'Subscription Plans',
      description: 'Subscription management and plans',
      flags: ['subscription_plans'],
      subscriptionPlan: 'free'
    },
    {
      id: 'notification_bell',
      name: 'Notification Bell',
      description: 'Notification bell and alerts',
      flags: ['notification_bell'],
      subscriptionPlan: 'free'
    },
    {
      id: 'merchandising',
      name: 'Merchandising',
      description: 'Merchandise store and products',
      flags: ['merchandising'],
      subscriptionPlan: 'premium'
    },
    {
      id: 'elearn',
      name: 'eLearn',
      description: 'Educational learning platform',
      flags: ['elearn'],
      subscriptionPlan: 'basic'
    },
    {
      id: 'learn_pro',
      name: 'eLearn Pro',
      description: 'Premium educational content and features',
      flags: ['learn_pro'],
      subscriptionPlan: 'premium'
    },
    {
      id: 'ai_guidance',
      name: 'AI Guidance',
      description: 'AI-powered financial guidance and coaching',
      flags: ['ai_guidance'],
      subscriptionPlan: 'premium'
    },
    {
      id: 'ai_coach',
      name: 'AI Coach',
      description: 'AI coaching and financial advice',
      flags: ['ai_coach'],
      subscriptionPlan: 'premium'
    },
    {
      id: 'ai_forecast',
      name: 'AI Forecast',
      description: 'AI-powered financial forecasting',
      flags: ['ai_forecast'],
      subscriptionPlan: 'premium'
    },
    {
      id: 'pro_invest',
      name: 'Pro Invest',
      description: 'Professional investment management',
      flags: ['pro_invest'],
      subscriptionPlan: 'professional'
    },
    {
      id: 'premium_gaming',
      name: 'Premium Gaming',
      description: 'Premium gaming features and challenges',
      flags: ['premium_gaming'],
      subscriptionPlan: 'premium'
    },
    {
      id: 'round_ups',
      name: 'Round Ups',
      description: 'Automatic savings round ups',
      flags: ['round_ups'],
      subscriptionPlan: 'basic'
    },
    {
      id: 'investment_fund',
      name: 'Investment Fund',
      description: 'Investment fund management',
      flags: ['investment_fund'],
      subscriptionPlan: 'professional'
    },
    {
      id: 'total_savings_transfer',
      name: 'Total Savings and Transfer',
      description: 'Comprehensive savings and transfer tools',
      flags: ['total_savings_transfer'],
      subscriptionPlan: 'basic'
    },
    {
      id: 'check_in',
      name: 'Check In',
      description: 'Regular financial check-in features',
      flags: ['check_in'],
      subscriptionPlan: 'free'
    }
  ];

  // ✅ Phase 2: Helper functions for pagination, search, and sort
  const getPaginatedData = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  const getTotalPages = (dataLength) => {
    return Math.ceil(dataLength / itemsPerPage);
  };

  const handleSort = (field) => {
    let direction = 'asc';
    if (sortConfig.field === field && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ field, direction });
    
    // Fetch users with new sorting
    fetchUsers(1, searchTerm, field, direction); // Reset to page 1 when sorting
    setUserPage(1);
  };

  const sortData = (data, config) => {
    if (!config.field) return data;
    
    return [...data].sort((a, b) => {
      const aValue = a[config.field] || '';
      const bValue = b[config.field] || '';
      
      if (config.direction === 'asc') {
        return aValue.toString().localeCompare(bValue.toString());
      } else {
        return bValue.toString().localeCompare(aValue.toString());
      }
    });
  };

  const filterUsers = (users, searchTerm) => {
    if (!searchTerm) return users;
    
    return users.filter(user => 
      (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.status || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.role || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const fetchFeatureFlags = async () => {
    try {
      console.log('🔍 Fetching feature flags with token:', adminToken ? 'Token available' : 'No token');
      const response = await fetch(`${API_BASE_URL}/api/admin/feature-flags`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🔍 Feature flags response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        setFeatureFlags(data.feature_flags || []);
        console.log('✅ Feature flags loaded successfully');
      } else {
        if (response.status === 401) {
          console.error('❌ Admin token expired or invalid - redirecting to login');
          localStorage.removeItem('admin_token');
          onLogout();
          return;
        }
        throw new Error(`HTTP ${response.status}: Failed to fetch feature flags`);
      }
    } catch (error) {
      console.error('❌ Error fetching feature flags:', error);
      setError('Failed to load feature flags - ' + error.message);
    }
  };

  // ✅ Phase 2: Enhanced fetchLogs with pagination support (1 year data)
  const fetchLogs = async (page = 1, eventType = 'all') => {
    try {
      console.log('🔍 Fetching logs with token:', adminToken ? 'Token available' : 'No token');
      
      // Calculate date range for last 1 year
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      const fromDate = oneYearAgo.toISOString().split('T')[0]; // YYYY-MM-DD format
      const toDate = new Date().toISOString().split('T')[0]; // Today
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: itemsPerPage.toString(),
        from_date: fromDate,
        to_date: toDate
      });
      
      if (eventType && eventType !== 'all') {
        params.append('event_type', eventType);
      }
      
      const response = await fetch(`${API_BASE_URL}/api/admin/logs?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🔍 Logs response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setLogPage(page);
        setLogTotalPages(data.total_pages || 1);
        
        // Extract unique event types for filtering (only on first load or when no filter)
        if (eventType === 'all') {
          const types = [...new Set((data.logs || []).map(log => log.event_type))];
          setEventTypes(['all', ...types]);
        }
        console.log('✅ Logs loaded successfully');
      } else {
        if (response.status === 401) {
          console.error('❌ Admin token expired or invalid - redirecting to login');
          localStorage.removeItem('admin_token');
          onLogout();
          return;
        }
        throw new Error(`HTTP ${response.status}: Failed to fetch logs`);
      }
    } catch (error) {
      console.error('❌ Error fetching logs:', error);
      setError('Failed to load logs - ' + error.message);
    }
  };

  // ✅ Phase 2: Enhanced fetchUsers with pagination, search, and sorting support  
  const fetchUsers = async (page = 1, search = '', sortBy = '', sortOrder = 'asc') => {
    try {
      console.log('🔍 Fetching users with token:', adminToken ? 'Token available' : 'No token');
      
      // Calculate date range for last 1 year
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      const fromDate = oneYearAgo.toISOString().split('T')[0]; // YYYY-MM-DD format
      const toDate = new Date().toISOString().split('T')[0]; // Today
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: itemsPerPage.toString(),
        from_date: fromDate,
        to_date: toDate
      });
      
      if (search) params.append('search', search);
      if (sortBy) params.append('sort_by', sortBy);
      if (sortOrder) params.append('sort_order', sortOrder);
      
      const response = await fetch(`${API_BASE_URL}/api/admin/users?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🔍 Users response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setUserPage(page);
        setUserTotalPages(data.total_pages || 1);
        setTotalItems(data.total_users || 0);
        console.log('✅ Users loaded successfully');
      } else {
        if (response.status === 401) {
          console.error('❌ Admin token expired or invalid - redirecting to login');
          localStorage.removeItem('admin_token');
          onLogout();
          return;
        }
        throw new Error(`HTTP ${response.status}: Failed to fetch users`);
      }
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      setError('Failed to load users - ' + error.message);
    }
  };

  const updateFeatureFlag = async (flagName, enabled) => {
    setUpdating(flagName);
    try {
      console.log(`Updating feature flag: ${flagName} to ${enabled}`);
      const response = await fetch(`${API_BASE_URL}/api/admin/feature-flags/${flagName}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({ enabled })
      });
      
      console.log(`Response status: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Feature flag update response:', data);
        await fetchFeatureFlags();
        setError('');
      } else {
        const errorData = await response.json();
        console.error('Feature flag update failed:', errorData);
        throw new Error(`Failed to update feature flag: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating feature flag:', error);
      setError(`Failed to update feature flag: ${error.message}`);
    } finally {
      setUpdating('');
    }
  };

  // ✅ Phase 2: Enhanced user management with soft/hard delete
  const updateUserStatus = async (userId, action) => {
    if (action === 'delete' || action === 'inactivate') {
      setUserToDelete(userId);
      setShowDeleteConfirmModal(true);
      return;
    }

    try {
      const endpoint = action === 'enable' ? 'enable' : 'disable';
      const response = await fetch(`${API_BASE_URL}/api/admin/user/${userId}/${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      if (response.ok) {
        await fetchUsers(currentPage);
        setError('');
      } else {
        throw new Error(`Failed to ${action} user`);
      }
    } catch (error) {
      console.error(`Error ${action} user:`, error);
      setError(`Failed to ${action} user`);
    }
  };

  const confirmDeleteUser = async () => {
    try {
      let endpoint, method;
      
      if (deleteMode === 'disable') {
        endpoint = `disable`;
        method = 'PUT';
      } else if (deleteMode === 'hard') {
        endpoint = `delete-permanent`;
        method = 'DELETE';
      } else {
        endpoint = `delete`;
        method = 'DELETE';
      }
      
      const response = await fetch(`${API_BASE_URL}/api/admin/user/${userToDelete}/${endpoint}`, {
        method: method,
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      if (response.ok) {
        await fetchUsers(userPage);
        setShowDeleteConfirmModal(false);
        setUserToDelete(null);
        setError('');
      } else {
        throw new Error(`Failed to ${deleteMode} user`);
      }
    } catch (error) {
      console.error(`Error ${deleteMode} user:`, error);
      setError(`Failed to ${deleteMode} user`);
    }
  };

  const updateUserRole = async (userId, role) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/user/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({ role })
      });
      
      if (response.ok) {
        await fetchUsers(currentPage);
        setError('');
      } else {
        throw new Error('Failed to update user role');
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      setError('Failed to update user role');
    }
  };

  // ✅ Phase 2: Update subscription plan for feature group
  const updateFeatureSubscriptionPlan = async (groupId, planValue) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/feature-groups/${groupId}/subscription-plan`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({ subscription_plan: planValue })
      });
      
      if (response.ok) {
        console.log('✅ Subscription plan updated successfully for', groupId, 'to', planValue);
        setError('');
      } else {
        throw new Error('Failed to update subscription plan');
      }
    } catch (error) {
      console.error('Error updating subscription plan:', error);
      setError('Failed to update subscription plan');
    }
  };

  useEffect(() => {
    if (adminToken) {
      console.log('🔍 AdminPanel useEffect - starting data fetch with token:', adminToken.substring(0, 20) + '...');
      console.log('🔍 API_BASE_URL:', API_BASE_URL);
      
      Promise.all([
        fetchFeatureFlags(),
        fetchLogs(logPage),
        fetchUsers(userPage)
      ]).finally(() => {
        console.log('🔍 All admin data fetch completed');
        setLoading(false);
      });
    } else {
      console.log('❌ No admin token available in useEffect');
      setLoading(false);
    }
  }, [adminToken, logPage, userPage]);

  // Debounced search effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (adminToken) {
        fetchUsers(1, searchTerm, sortConfig.field, sortConfig.direction);
        setUserPage(1);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, adminToken]);

  // Event type filter effect
  useEffect(() => {
    if (adminToken) {
      fetchLogs(1, selectedEventType);
      setLogPage(1);
    }
  }, [selectedEventType, adminToken]);

  // ✅ Phase 2: Use server-side sorted data directly
  const processedUsers = users; // Server handles sorting across entire dataset
  const paginatedUsers = users;
  
  const filteredLogs = logs; // Server-side pagination and filtering  
  const paginatedLogs = logs;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <Settings className="h-12 w-12 mx-auto mb-4 animate-spin" style={{color: '#009688'}} />
          <p style={{color: '#D4AF37'}} className="text-lg">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Shield className="h-8 w-8" style={{color: '#009688'}} />
            <h1 className="text-3xl font-bold" style={{color: '#D4AF37'}}>Admin Control Panel</h1>
          </div>
          <Button 
            onClick={onLogout}
            variant="outline"
            className="border-red-500 text-red-400 hover:bg-red-500 hover:text-white"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>

        {error && (
          <Alert className="mb-6 border-red-500 bg-red-900/20">
            <AlertDescription className="text-red-300">{error}</AlertDescription>
          </Alert>
        )}

        {/* Tabs Navigation */}
        <div className="flex space-x-4 mb-8 border-b border-gray-700">
          <button
            onClick={() => setCurrentTab('feature-flags')}
            className={`px-6 py-3 font-medium transition-colors ${
              currentTab === 'feature-flags' 
                ? 'text-teal-400 border-b-2 border-teal-400' 
                : 'text-gray-400 hover:text-teal-300'
            }`}
          >
            Feature Flags
          </button>
          <button
            onClick={() => setCurrentTab('api-logs')}
            className={`px-6 py-3 font-medium transition-colors ${
              currentTab === 'api-logs' 
                ? 'text-teal-400 border-b-2 border-teal-400' 
                : 'text-gray-400 hover:text-teal-300'
            }`}
          >
            API Logs
          </button>
          <button
            onClick={() => setCurrentTab('user-management')}
            className={`px-6 py-3 font-medium transition-colors ${
              currentTab === 'user-management' 
                ? 'text-teal-400 border-b-2 border-teal-400' 
                : 'text-gray-400 hover:text-teal-300'
            }`}
          >
            User Management
          </button>
        </div>

        {/* Feature Flags Tab */}
        {currentTab === 'feature-flags' && (
          <Card className="backdrop-blur-lg bg-gradient-to-br from-gray-900/80 to-black/60 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Feature Flag Management</CardTitle>
              <CardDescription className="text-gray-400">
                Control which features are enabled for the application. Master flag controls all features, defaulting to false.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {featureGroups.map((group) => (
                  <Card key={group.id} className={`border ${group.isMaster ? 'border-red-500/50 bg-red-900/10' : 'border-gray-700 bg-gray-800/50'}`}>
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <CardTitle className={`text-lg ${group.isMaster ? 'text-red-400' : 'text-white'}`}>
                            {group.name}
                            {group.isMaster && <Badge className="ml-2 bg-red-600/20 text-red-300">Master</Badge>}
                          </CardTitle>
                          <CardDescription className="text-gray-400 mt-1">
                            {group.description}
                          </CardDescription>
                        </div>
                        <div className="flex items-center space-x-4">
                          {/* ✅ Phase 2: Subscription Plan Dropdown - Hidden for Master Flag */}
                          {!group.isMaster && (
                            <select 
                              value={group.subscriptionPlan || 'free'}
                              onChange={(e) => updateFeatureSubscriptionPlan(group.id, e.target.value)}
                              className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-1 text-sm"
                            >
                              {subscriptionPlans.map(plan => (
                                <option key={plan.value} value={plan.value}>
                                  {plan.label}
                                </option>
                              ))}
                            </select>
                          )}
                          <Switch
                            checked={featureFlags.some(flag => 
                              group.flags.includes(flag.flag_name) && flag.enabled
                            )}
                            onCheckedChange={(checked) => {
                              group.flags.forEach(flagName => {
                                updateFeatureFlag(flagName, checked);
                              });
                            }}
                            disabled={updating === group.flags[0]}
                          />
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* API Logs Tab */}
        {currentTab === 'api-logs' && (
          <Card className="backdrop-blur-lg bg-gradient-to-br from-gray-900/80 to-black/60 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">API Event Logs</CardTitle>
              <CardDescription className="text-gray-400">
                Monitor API requests and system events (30-day rotation, latest on top)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* ✅ Phase 2: Event Type Filter */}
              <div className="mb-6">
                <Label className="text-white mb-2 block">Filter by Event Type:</Label>
                <select 
                  value={selectedEventType}
                  onChange={(e) => setSelectedEventType(e.target.value)}
                  className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2"
                >
                  {eventTypes.map(type => (
                    <option key={type} value={type}>
                      {type === 'all' ? 'All Events' : 
                       type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>

              {/* ✅ Phase 2: Logs Table with Pagination */}
              <div className="overflow-x-auto">
                <table className="w-full table-auto">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-teal-400 font-medium">Timestamp</th>
                      <th className="text-left py-3 px-4 text-teal-400 font-medium">Method</th>
                      <th className="text-left py-3 px-4 text-teal-400 font-medium">Path</th>
                      <th className="text-left py-3 px-4 text-teal-400 font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-teal-400 font-medium">Event Type</th>
                      <th className="text-left py-3 px-4 text-teal-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedLogs.map((log, index) => (
                      <tr key={index} className="border-b border-gray-800 hover:bg-gray-800/30">
                        <td className="py-3 px-4 text-gray-300 text-sm">
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={`text-xs ${
                            log.method === 'GET' ? 'bg-blue-600/20 text-blue-300' :
                            log.method === 'POST' ? 'bg-green-600/20 text-green-300' :
                            log.method === 'PUT' ? 'bg-yellow-600/20 text-yellow-300' :
                            log.method === 'DELETE' ? 'bg-red-600/20 text-red-300' :
                            'bg-gray-600/20 text-gray-300'
                          }`}>
                            {log.method}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-gray-300 text-sm font-mono">
                          {log.path}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={`text-xs ${
                            log.status_code >= 200 && log.status_code < 300 ? 'bg-green-600/20 text-green-300' :
                            log.status_code >= 400 && log.status_code < 500 ? 'bg-yellow-600/20 text-yellow-300' :
                            log.status_code >= 500 ? 'bg-red-600/20 text-red-300' :
                            'bg-gray-600/20 text-gray-300'
                          }`}>
                            {log.status_code}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-gray-300 text-sm">
                          {log.event_type || '-'}
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            onClick={() => {
                              setSelectedLog(log);
                              setShowLogDetailModal(true);
                            }}
                            variant="outline"
                            size="sm"
                            className="border-gray-600 text-gray-300 hover:bg-gray-700"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ✅ Phase 2: Pagination Controls for Logs */}
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-400">
                  Page {logPage} of {logTotalPages} - Showing {logs.length} logs
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={() => fetchLogs(Math.max(logPage - 1, 1), selectedEventType)}
                    disabled={logPage === 1}
                    variant="outline"
                    size="sm"
                    className="border-gray-600 text-gray-300"
                  >
                    Previous
                  </Button>
                  
                  {/* Page numbers */}
                  <div className="flex space-x-1">
                    {Array.from({ length: Math.min(5, logTotalPages) }, (_, i) => {
                      const page = Math.max(1, logPage - 2) + i;
                      if (page <= logTotalPages) {
                        return (
                          <Button
                            key={page}
                            onClick={() => fetchLogs(page, selectedEventType)}
                            variant={page === logPage ? "default" : "outline"}
                            size="sm"
                            className={page === logPage ? 
                              "bg-teal-600 text-white" : 
                              "border-gray-600 text-gray-300"
                            }
                          >
                            {page}
                          </Button>
                        );
                      }
                      return null;
                    })}
                  </div>
                  
                  <Button
                    onClick={() => fetchLogs(Math.min(logPage + 1, logTotalPages), selectedEventType)}
                    disabled={logPage === logTotalPages}
                    variant="outline"
                    size="sm"
                    className="border-gray-600 text-gray-300"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* User Management Tab */}
        {currentTab === 'user-management' && (
          <Card className="backdrop-blur-lg bg-gradient-to-br from-gray-900/80 to-black/60 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">User Management</CardTitle>
              <CardDescription className="text-gray-400">
                View and manage all users in the system
                {users.length > 0 && (
                  <div className="mt-2 space-x-4">
                    <span className="text-green-400">
                      Total: {users.length}
                    </span>
                    <span className="text-blue-400">
                      Registered: {users.filter(u => u.user_type === 'registered').length}
                    </span>
                    <span className="text-yellow-400">
                      Invited: {users.filter(u => u.user_type === 'invited').length}
                    </span>
                  </div>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* ✅ Phase 2: Search Bar */}
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder="Search by name, email, status, or role..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-teal-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* ✅ Phase 2: Users Table with Sorting */}
              <div className="overflow-x-auto">
                <table className="w-full table-auto">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th 
                        className="text-left py-3 px-3 text-teal-400 font-medium cursor-pointer hover:text-teal-300"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center">
                          Name
                          {sortConfig.field === 'name' && (
                            sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="text-left py-3 px-3 text-teal-400 font-medium cursor-pointer hover:text-teal-300"
                        onClick={() => handleSort('email')}
                      >
                        <div className="flex items-center">
                          Email
                          {sortConfig.field === 'email' && (
                            sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
                      <th className="text-left py-3 px-3 text-teal-400 font-medium">Level</th>
                      <th className="text-left py-3 px-3 text-teal-400 font-medium">XP</th>
                      <th className="text-left py-3 px-3 text-teal-400 font-medium">Expenses</th>
                      <th className="text-left py-3 px-3 text-teal-400 font-medium">Friends</th>
                      <th 
                        className="text-left py-3 px-3 text-teal-400 font-medium cursor-pointer hover:text-teal-300"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center">
                          Status
                          {sortConfig.field === 'status' && (
                            sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="text-left py-3 px-3 text-teal-400 font-medium cursor-pointer hover:text-teal-300"
                        onClick={() => handleSort('role')}
                      >
                        <div className="flex items-center">
                          Role
                          {sortConfig.field === 'role' && (
                            sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />
                          )}
                        </div>
                      </th>
                      <th className="text-left py-3 px-3 text-teal-400 font-medium">Created</th>
                      <th className="text-left py-3 px-3 text-teal-400 font-medium">Last Login</th>
                      <th className="text-left py-3 px-3 text-teal-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.map((user, index) => (
                      <tr key={user.user_id || index} className={`border-b border-gray-800 hover:bg-gray-800/30 ${
                        user.user_type === 'invited' ? 'bg-blue-900/10' : ''
                      }`}>
                        <td className="py-2 px-3 text-white font-medium">
                          {user.name || 'Unknown'}
                          {user.user_type === 'invited' && (
                            <Badge className="ml-2 bg-blue-600/20 text-blue-300 text-xs">
                              Invited
                            </Badge>
                          )}
                        </td>
                        <td className="py-2 px-3 text-gray-300 text-xs">{user.email}</td>
                        <td className="py-2 px-3">
                          <Badge className="bg-purple-600/20 text-purple-300 text-xs">
                            {user.user_type === 'invited' ? '-' : (user.level || 1)}
                          </Badge>
                        </td>
                        <td className="py-2 px-3 text-gray-300 text-xs">
                          {user.user_type === 'invited' ? '-' : (user.xp || 0)}
                        </td>
                        <td className="py-2 px-3 text-gray-300 text-xs">
                          {user.stats?.expense_count || 0}
                        </td>
                        <td className="py-2 px-3 text-gray-300 text-xs">
                          {user.stats?.friend_count || 0}
                        </td>
                        <td className="py-2 px-3">
                          <Badge className={`text-xs ${
                            user.user_type === 'invited' ? 'bg-blue-600/20 text-blue-300' :
                            user.status === 'deleted' ? 'bg-red-600/20 text-red-300' :
                            user.status === 'disabled' ? 'bg-yellow-600/20 text-yellow-300' :
                            'bg-green-600/20 text-green-300'
                          }`}>
                            {user.user_type === 'invited' ? 'invited' : (user.status || 'active')}
                          </Badge>
                        </td>
                        <td className="py-2 px-3">
                          {user.user_type === 'invited' ? (
                            <span className="text-gray-500 text-xs">N/A</span>
                          ) : (
                            <select 
                              value={user.role || 'user'}
                              onChange={(e) => updateUserRole(user.user_id, e.target.value)}
                              className="bg-gray-800 text-white border border-gray-600 rounded px-2 py-1 text-xs"
                            >
                              <option value="user">User</option>
                              <option value="moderator">Moderator</option>
                              <option value="admin">Admin</option>
                            </select>
                          )}
                        </td>
                        <td className="py-2 px-3 text-gray-400 text-xs">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="py-2 px-3 text-gray-400 text-xs">
                          {user.user_type === 'invited' ? 'Pending signup' : 
                           (user.stats?.last_login ? new Date(user.stats.last_login).toLocaleDateString() : 'Never')}
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex space-x-1">
                            {user.user_type === 'invited' ? (
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-400">
                                  Invited by: {user.invitation_details?.sender_name}
                                </span>
                              </div>
                            ) : (
                              <>
                                {user.status === 'active' ? (
                                  <Button
                                    onClick={() => updateUserStatus(user.user_id, 'disable')}
                                    variant="outline"
                                    size="sm"
                                    className="border-yellow-600 text-yellow-300 hover:bg-yellow-600/30"
                                  >
                                    <UserMinus className="h-3 w-3 mr-1" />
                                    Disable
                                  </Button>
                                ) : user.status === 'disabled' ? (
                                  <Button
                                    onClick={() => updateUserStatus(user.user_id, 'enable')}
                                    variant="outline"
                                    size="sm"
                                    className="border-green-600 text-green-300 hover:bg-green-600/30"
                                  >
                                    <UserCheck className="h-3 w-3 mr-1" />
                                    Enable
                                  </Button>
                                ) : null}
                                
                                {user.status !== 'deleted' && (
                                  <Button
                                    onClick={() => updateUserStatus(user.user_id, 'inactivate')}
                                    variant="outline"
                                    size="sm"
                                    className="border-red-600 text-red-300 hover:bg-red-600/30"
                                  >
                                    <Trash2 className="h-3 w-3 mr-1" />
                                    Inactivate
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ✅ Phase 2: Pagination Controls for Users */}
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-400">
                  Page {userPage} of {userTotalPages} - Showing {users.length} of {totalItems} users
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={() => fetchUsers(Math.max(userPage - 1, 1), searchTerm, sortConfig.field, sortConfig.direction)}
                    disabled={userPage === 1}
                    variant="outline"
                    size="sm"
                    className="border-gray-600 text-gray-300"
                  >
                    Previous
                  </Button>
                  
                  {/* Page numbers */}
                  <div className="flex space-x-1">
                    {Array.from({ length: Math.min(5, userTotalPages) }, (_, i) => {
                      const page = Math.max(1, userPage - 2) + i;
                      if (page <= userTotalPages) {
                        return (
                          <Button
                            key={page}
                            onClick={() => fetchUsers(page, searchTerm, sortConfig.field, sortConfig.direction)}
                            variant={page === userPage ? "default" : "outline"}
                            size="sm"
                            className={page === userPage ? 
                              "bg-teal-600 text-white" : 
                              "border-gray-600 text-gray-300"
                            }
                          >
                            {page}
                          </Button>
                        );
                      }
                      return null;
                    })}
                  </div>
                  
                  <Button
                    onClick={() => fetchUsers(Math.min(userPage + 1, userTotalPages), searchTerm, sortConfig.field, sortConfig.direction)}
                    disabled={userPage === userTotalPages}
                    variant="outline"
                    size="sm"
                    className="border-gray-600 text-gray-300"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ✅ Phase 2: Log Detail Modal */}
      <Dialog open={showLogDetailModal} onOpenChange={setShowLogDetailModal}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-white">API Log Details</DialogTitle>
            <DialogDescription className="text-gray-400">
              Detailed information about this API request
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-teal-400">Timestamp</Label>
                  <p className="text-gray-300">{new Date(selectedLog.timestamp).toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-teal-400">Method & Status</Label>
                  <p className="text-gray-300">{selectedLog.method} - {selectedLog.status_code}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-teal-400">Path</Label>
                  <p className="text-gray-300 font-mono">{selectedLog.path}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-teal-400">User Agent</Label>
                  <p className="text-gray-300 text-sm">{selectedLog.user_agent || 'N/A'}</p>
                </div>
              </div>
              
              {selectedLog.error && (
                <div>
                  <Label className="text-teal-400">Error Stack Trace (20-25 lines)</Label>
                  <pre className="bg-black/50 border border-gray-700 rounded p-4 text-red-300 text-sm overflow-auto max-h-60">
                    {selectedLog.error}
                  </pre>
                </div>
              )}
              
              {selectedLog.response_data && (
                <div>
                  <Label className="text-teal-400">Response Data</Label>
                  <pre className="bg-black/50 border border-gray-700 rounded p-4 text-green-300 text-sm overflow-auto max-h-60">
                    {JSON.stringify(selectedLog.response_data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ✅ Phase 2: Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirmModal} onOpenChange={setShowDeleteConfirmModal}>
        <DialogContent className="bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">User Action</DialogTitle>
            <DialogDescription className="text-gray-400">
              Choose what action to take on this user account.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-3">
              <label className="flex items-center space-x-3">
                <input 
                  type="radio" 
                  name="deleteMode" 
                  value="disable" 
                  checked={deleteMode === 'disable'}
                  onChange={(e) => setDeleteMode(e.target.value)}
                  className="text-yellow-400"
                />
                <div>
                  <p className="text-yellow-400 font-medium">Disable Account</p>
                  <p className="text-gray-400 text-sm">Temporarily disable user login while keeping all data</p>
                </div>
              </label>
              
              <label className="flex items-center space-x-3">
                <input 
                  type="radio" 
                  name="deleteMode" 
                  value="soft" 
                  checked={deleteMode === 'soft'}
                  onChange={(e) => setDeleteMode(e.target.value)}
                  className="text-orange-400"
                />
                <div>
                  <p className="text-orange-400 font-medium">Soft Delete</p>
                  <p className="text-gray-400 text-sm">Mark user as deleted but keep data for audit purposes</p>
                </div>
              </label>
              
              <label className="flex items-center space-x-3">
                <input 
                  type="radio" 
                  name="deleteMode" 
                  value="hard" 
                  checked={deleteMode === 'hard'}
                  onChange={(e) => setDeleteMode(e.target.value)}
                  className="text-red-400"
                />
                <div>
                  <p className="text-red-400 font-medium">Hard Delete (Permanent)</p>
                  <p className="text-gray-400 text-sm">Permanently remove all user data from database - IRREVERSIBLE</p>
                </div>
              </label>
            </div>
            
            <div className="flex space-x-3 pt-4">
              <Button
                onClick={() => setShowDeleteConfirmModal(false)}
                variant="outline"
                className="border-gray-600 text-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteUser}
                variant="outline"
                className={
                  deleteMode === 'hard' ? "border-red-600 text-red-300 hover:bg-red-600/30" : 
                  deleteMode === 'soft' ? "border-orange-600 text-orange-300 hover:bg-orange-600/30" :
                  "border-yellow-600 text-yellow-300 hover:bg-yellow-600/30"
                }
              >
                {deleteMode === 'hard' ? 'Permanently Delete' : 
                 deleteMode === 'soft' ? 'Soft Delete' : 
                 'Disable Account'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;